// Picker INLINE de vistas solo-producto para el modo "Fotos de producto".
//
// A diferencia del `VistaPickerFullscreen` del Catálogo IA —que es un STEP
// propio, con pills de categoría, poses con-modelo y footer de confirmación—
// acá las vistas son pocas (≤6 por producto, ya filtradas por `model_view`),
// así que entran todas en una grilla dentro del panel de configuración. Sin
// "Ver más", sin overlay: el usuario ve el catálogo completo de una.
//
// Multi-select: cada vista elegida es UNA imagen generada (un request propio,
// ver ADR-2026-08-13-fotos-producto-fan-out-por-vista).

import React from 'react';
import { Check, Sparkles } from 'lucide-react';
import type { VistaProductoOption } from '../vistasCatalogo';

export interface VistaProductoPickerProps {
  /** Vistas compatibles con el producto elegido, con thumb ya resuelto
   *  (`vistasSoloProductoPara`). */
  vistas: VistaProductoOption[];
  /** Ids (slugs) de las vistas seleccionadas. */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Columnas de la grilla. Desktop usa 4; mobile 3. */
  columns?: 3 | 4;
  /** Id del contenedor de la grilla — target del scroll+shake cuando el
   *  usuario toca Generar sin ninguna vista elegida. El sheet monta las dos
   *  variantes a la vez, así que el id lo provee el caller con su prefijo. */
  gridId?: string;
}

export const VistaProductoPicker: React.FC<VistaProductoPickerProps> = ({
  vistas,
  selectedIds,
  onChange,
  columns = 3,
  gridId,
}) => {
  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((v) => v !== id)
        : [...selectedIds, id],
    );
  };

  if (vistas.length === 0) {
    // Defensivo: `isVistaCompatibleConModelView` devuelve TODAS las vistas
    // cuando el producto no tiene `model_view`, así que llegar acá implicaría
    // un catálogo vacío. Mejor un texto que una grilla fantasma.
    return (
      <p className="text-[13px] text-[#3C3C43]/60 leading-relaxed">
        No hay vistas disponibles para este producto.
      </p>
    );
  }

  return (
    <div
      id={gridId}
      className={`grid gap-2.5 ${
        columns === 4 ? 'grid-cols-4' : 'grid-cols-3'
      }`}
    >
      {vistas.map((vista) => {
        const selected = selectedIds.includes(vista.id);
        return (
          <button
            key={vista.id}
            type="button"
            onClick={() => toggle(vista.id)}
            aria-pressed={selected}
            className={`relative aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 active:scale-[0.98] transition-all ${
              selected
                ? 'ring-2 ring-brand-primary'
                : 'ring-1 ring-black/[0.06]'
            }`}
          >
            {vista.thumbUrl ? (
              <img
                src={vista.thumbUrl}
                alt={vista.nombre}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              // Vistas sin foto de ejemplo (hoy solo "Automática"): ícono +
              // nombre centrados en vez del placeholder "sin preview", que
              // dejaba una card muerta al lado de las que sí tienen foto.
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-2 text-center">
                <Sparkles className="w-5 h-5 text-neutral-400" />
                <span className="text-[12px] font-medium text-brand-black/80 leading-tight">
                  {vista.nombre}
                </span>
              </div>
            )}
            {/* El gradiente + nombre en blanco solo tienen sentido sobre la
                foto; sin thumb taparían el texto oscuro de arriba. */}
            {vista.thumbUrl && (
              <>
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
                <p className="absolute bottom-2 left-2 right-2 text-left text-white text-[12.5px] font-medium leading-tight drop-shadow">
                  {vista.nombre}
                </p>
              </>
            )}
            {selected && (
              <span className="absolute top-2 right-2 w-6 h-6 inline-flex items-center justify-center rounded-full bg-brand-primary text-white">
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
